import { useValue } from "@legendapp/state/react";
import { Icon } from "@repo/ui/components/icon";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@repo/ui/ui/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/ui/ui/dropdown-menu";
import type { ReactElement } from "react";
import { useState } from "react";

import { BoardShareDialog } from "~/components/app/board-share-dialog";
import { boards$ } from "~/stores/boards";
import { deleteBoard, duplicateBoard } from "~/stores/boards/boards.actions";

export const BoardOptions = ({
  boardId,
  children,
  type = "dropdown",
  showShare = true,
}: {
  boardId: string;
  children: ReactElement;
  type?: "dropdown" | "context";
  /**
   * Whether to render the "Share" menu item. The topbar surfaces a
   * dedicated Share button next to this menu, so it opts out here.
   */
  showShare?: boolean;
}) => {
  const [shareOpen, setShareOpen] = useState(false);
  const favorite = useValue(boards$[boardId].favorite);
  const Menu = type === "dropdown" ? DropdownMenu : ContextMenu;
  const MenuTrigger =
    type === "dropdown" ? DropdownMenuTrigger : ContextMenuTrigger;
  const MenuContent =
    type === "dropdown" ? DropdownMenuContent : ContextMenuContent;
  const MenuItem = type === "dropdown" ? DropdownMenuItem : ContextMenuItem;
  const Separator =
    type === "dropdown" ? DropdownMenuSeparator : ContextMenuSeparator;

  return (
    <>
      <Menu>
        <MenuTrigger render={children} />
        <MenuContent align="end">
          {showShare && (
            <MenuItem onClick={() => setShareOpen(true)}>
              <Icon name="arrow-up-bracket" />
              Share
            </MenuItem>
          )}
          <MenuItem onClick={() => boards$[boardId].favorite.toggle()}>
            <Icon name={favorite ? "star-disabled" : "star"} />
            {favorite ? "Unfavorite" : "Favorite"}
          </MenuItem>
          <MenuItem onClick={() => duplicateBoard(boardId)}>
            <Icon name="duplicate" />
            Duplicate
          </MenuItem>
          <Separator />
          <MenuItem variant="destructive" onClick={() => deleteBoard(boardId)}>
            <Icon name="trash" />
            Delete
          </MenuItem>
        </MenuContent>
      </Menu>
      {showShare && (
        <BoardShareDialog
          boardId={boardId}
          open={shareOpen}
          onOpenChange={setShareOpen}
        />
      )}
    </>
  );
};
